"use client";
import { auth, database } from "@/lib/firebase-util";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { BiMenu } from "react-icons/bi";
import {
    RiBookmarkLine,
    RiBookOpenLine,
    RiFileListLine,
    RiHome4Line,
    RiLoginBoxLine,
    RiLogoutBoxLine,
    RiMoneyDollarCircleLine,
    RiShoppingCartLine
} from "react-icons/ri";
import HKAICTLogo from "../app/icon.png";
import LogoutButton from "./Login/LogoutButton";
import ThemeToggle from "./ThemeToggle";

type NavItem = {
    href: string;
    label: string;
    icon: React.ReactNode;
    showWhen?: 'loggedIn' | 'loggedOut' | 'always';
    badge?: number;
};

export default function NavBar({
    isLoggedIn = false,
}: {
    isLoggedIn: boolean;
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [cartItemsCount, setCartItemsCount] = useState(0);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(isLoggedIn);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsUserLoggedIn(!!user);

        });

        return () => unsubscribe();
    }, []);

    const toggleMenu = () => setMenuOpen(!menuOpen);

    // Close when navigated away
    const pathname = usePathname()
    const searchParams = useSearchParams()

    useEffect(() => {
        setMenuOpen(false);
    }, [pathname, searchParams,])

    // Handle shopping cart changes
    useEffect(() => {
        if (!isUserLoggedIn) {
            setCartItemsCount(0);
            return;
        }

        const user = auth.currentUser;
        if (!user) return;

        const shoppingCartRef = doc(database, 'shoppingCart', user.uid);

        const unsubscribe = onSnapshot(shoppingCartRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                const items = data?.items ?? [];
                setCartItemsCount(Array.isArray(items) ? items.length : 0);
            } else {
                setCartItemsCount(0);
            }
        }, (error) => {
            console.error('Error listening to cart updates:', error);
            setCartItemsCount(0);
        });

        return () => unsubscribe();
    }, [isUserLoggedIn]);

    const navItems: NavItem[] = [
        {
            href: "/main",
            label: "主頁",
            icon: <RiHome4Line className="w-5 h-5" />,
            showWhen: 'loggedIn'
        },
        {
            href: "/courses",
            label: "線上課程",
            icon: <RiBookOpenLine className="w-5 h-5" />,
            showWhen: 'always'
        },
        {
            href: "/notes",
            label: "筆記與練習",
            icon: <RiFileListLine className="w-5 h-5" />,
            showWhen: 'always'
        },
        {
            href: "/subsidy",
            label: "學費資助介紹",
            icon: <RiMoneyDollarCircleLine className="w-5 h-5" />,
            showWhen: 'loggedOut'
        },
        {
            href: "/shopping-cart",
            label: "購物車",
            icon: <RiShoppingCartLine className="w-5 h-5" />,
            showWhen: 'loggedIn',
            badge: cartItemsCount
        },
        {
            href: "/main/subsidy",
            label: "學費資助申請",
            icon: <RiMoneyDollarCircleLine className="w-5 h-5" />,
            showWhen: 'loggedIn'
        },
        {
            href: "/suggested-courses",
            label: "課程與考核範圍",
            icon: <RiBookmarkLine className="w-5 h-5" />,
            showWhen: 'always'
        }
    ];

    const shouldShowItem = (item: NavItem) => {
        if (item.showWhen === 'always') return true;
        if (item.showWhen === 'loggedIn' && isUserLoggedIn) return true;
        if (item.showWhen === 'loggedOut' && !isUserLoggedIn) return true;
        return false;
    };

    return (
        <nav className="flex sticky top-0 z-50 justify-between items-center p-5 w-full h-20 via-red-900 to-rose-800 bg-linear-to-br from-rose-950">
            <span className="font-bold text-white uppercase">
                <Link
                    href="/"
                    className="flex gap-2 justify-center items-center group"
                >
                    <div className="relative w-10 h-10 rounded group-hover:bg-neutral-100 transition-normal">
                        <Image
                            src={HKAICTLogo}
                            fill
                            alt="Logo of HKAICT"
                            sizes="10vw"
                        />
                    </div>
                    <span className="text-neutral-300 transition-normal group-hover:text-neutral-100">
                        HKAICT | 香港 ICT 學苑
                    </span>
                </Link>
            </span>
            <button
                className="p-3 text-white rounded border lg:hidden"
                onClick={toggleMenu}
            >
                <BiMenu />
            </button>

            {menuOpen && (
                <button
                    className="absolute top-0 left-0 w-screen h-screen bg-transparent -z-10"
                    onClick={() => setMenuOpen(false)}
                />
            )}

            <div
                className={`absolute flex-wrap z-10 items-center justify-center block w-full gap-3 lg:max-h-full -mx-5 text-white lg:divide-y-0 bg-linear-to-bl from-rose-800 via-red-900 to-rose-950 lg:p-0 lg:w-fit lg:bg-none lg:mx-0 lg:relative lg:flex top-20 lg:top-0 overflow-hidden transition-normal ${menuOpen ? "p-5" : "p-0 max-h-0"}`}
            >
                <ThemeToggle />

                {!isUserLoggedIn && (
                    <Link
                        className="flex gap-2 items-center my-2 text-neutral-300 hover:text-neutral-100 hover:scale-105 transition-normal"
                        href="/login"
                    >
                        <RiLoginBoxLine className="w-5 h-5" />
                        登入／註冊
                    </Link>
                )}

                {navItems.map((item) =>
                    shouldShowItem(item) && (
                        <Link
                            key={item.href}
                            className="flex gap-2 items-center my-2 text-neutral-300 hover:text-neutral-100 hover:scale-105 transition-normal"
                            href={item.href}
                        >
                            {item.icon}
                            {item.label}
                            {item.badge !== undefined && item.badge > 0 && (
                                <span className="flex justify-center items-center -ml-1 w-5 h-5 text-xs font-bold text-white bg-red-400 rounded-full">
                                    {item.badge}
                                </span>
                            )}
                        </Link>
                    )
                )}

                {isUserLoggedIn && (
                    <LogoutButton className="flex gap-2 items-center my-2 text-neutral-300 hover:text-neutral-100 hover:scale-105 transition-normal">
                        <RiLogoutBoxLine className="w-5 h-5" />
                        登出
                    </LogoutButton>
                )}
            </div>
        </nav>
    );
}
